import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { ModalWrapper } from './ModalWrapper';
import { setEditFormModal } from '../../store/actions/modals';
import { useFetchForms } from '../../hooks/useFetchForms';
import { useFetchAsanaProjects } from '../../hooks/useFetchAsanaProjects';
import { useFetchSlackChannels } from '../../hooks/useFetchSlackChannels';
import { useEditFormMutation } from '../../services/api';
import { formIdSelector } from '../../store/selectors/general';
import { validateNewForm } from '../../validators';
import { SectionLoader, ButtonLoader } from '../general/Loaders';
import { BasicInfo } from '../forms/BasicInfo';
import { FieldPreferences } from '../forms/FieldPreferences';
import { StylesPreferences } from '../forms/StylesPreferences';
import { Button } from '../general/Button';
import { errorToast, successToast } from '../../utils/toasts';

export const EditFormModal = () => {
  const dispatch = useDispatch();

  const formId = useSelector(formIdSelector);

  const [editingForm, setEditingForm] = useState(false);

  const { forms, isLoading: formDataLoading } = useFetchForms({ id: formId });

  const {
    projects,
    isLoading: asanaProjectsLoading,
    isError: asanaProjectsError,
  } = useFetchAsanaProjects();

  const {
    channels,
    isLoading: slackChannelsLoading,
    isError: slackChannelsError,
  } = useFetchSlackChannels();

  const [editFormTrigger] = useEditFormMutation();

  const handleEditForm = values => {
    setEditingForm(true);
    editFormTrigger({
      ...values,
      id: formId,
      asanaProjectId: values?.asanaProject?.value,
      asanaAssigneeId: values?.asanaAssignee?.value || null,
      slackChannelId: values?.slackChannel?.value || null,
    })
      .unwrap()
      .then(_ => {
        dispatch(setEditFormModal({ showModal: false, formId: null }));
        successToast('Form data updated successfully!');
      })
      .catch(error => {
        console.error(error);
        errorToast('Unable to edit form data. Please try again.');
      })
      .finally(_ => {
        setEditingForm(false);
      });
  };

  const formik = useFormik({
    initialValues: {
      // basic info about the site and integrations
      siteUrl: '',
      siteName: '',
      asanaProject: {},
      asanaAssignee: {},
      slackChannel: '',
      subscribers: [],

      // field preferences
      formTitle: '',
      formDescription: '',
      // initializing the form fields with default values to use in to create the asana task
      formFields: [
        {
          id: 'userEmail',
          type: 'email',
          label: 'Please provide your email address:',
          required: true,
        },
        {
          id: 'asanaTaskName',
          type: 'textarea',
          label: 'Please share a short description:',
          required: true,
        },
      ],

      // styles preferences
      formColor: '#00bcd4',
      formFont: 'Open Sans',
      formLogo: '',
    },
    validate: validateNewForm,
    onSubmit: handleEditForm,
  });

  // Initialize formik initial values with the feedback fields from the form
  useEffect(() => {
    if (forms && forms[0]) {
      const selectedForm = forms[0];

      formik.setValues({
        siteUrl: selectedForm?.site_url || '',
        displaySiteUrl: selectedForm?.feedback_forms?.[0]?.display_site_url ?? false,
        siteName: selectedForm?.site_name || '',
        asanaProject: projects?.find(p => p.gid === selectedForm?.asana_project_id)
          ? {
              value: selectedForm.asana_project_id,
              label: projects.find(p => p.gid === selectedForm.asana_project_id)?.name,
            }
          : {},
        asanaAssignee: projects
          ?.find(p => p.gid === selectedForm?.asana_project_id)
          ?.members?.find(m => m.gid === selectedForm?.asana_assignee_id)
          ? {
              value: selectedForm.asana_assignee_id,
              label: projects
                .find(p => p.gid === selectedForm.asana_project_id)
                .members.find(m => m.gid === selectedForm.asana_assignee_id)?.name,
            }
          : {},
        slackChannel: channels.find(c => c.id === selectedForm?.slack_channel_id)
          ? {
              value: selectedForm.slack_channel_id,
              label: channels.find(c => c.id === selectedForm.slack_channel_id)?.name,
            }
          : {},
        subscribers: selectedForm?.subscribers?.map(subscriber => subscriber.email) || [],
        formTitle: selectedForm?.feedback_forms?.[0]?.title || '',
        formDescription: selectedForm?.feedback_forms?.[0]?.description || '',
        formFields: selectedForm?.feedback_forms?.[0]?.fields || [],
        formColor: selectedForm?.feedback_forms?.[0]?.color || '#00bcd4',
        formFont: selectedForm?.feedback_forms?.[0]?.font || 'Open Sans',
        formLogo: '',
      });
    }
  }, [forms]);

  if (formDataLoading || asanaProjectsLoading || slackChannelsLoading) {
    return (
      <ModalWrapper
        medium
        onClose={() => dispatch(setEditFormModal({ showModal: false, formId: null }))}
      >
        <div className="flex flex-col items-center gap-4 my-12">
          <SectionLoader dotColor="slate" />
          <p>Loading...</p>
        </div>
      </ModalWrapper>
    );
  }

  if (asanaProjectsError) {
    errorToast('Unable to fetch asana projects. Please try again later.');
    return;
  }

  if (slackChannelsError) {
    errorToast('Unable to fetch slack channels. Please try again later.');
    return;
  }

  const breakLineStyle = 'border border-navy w-full my-8';

  return (
    <ModalWrapper
      medium
      onClose={() => dispatch(setEditFormModal({ showModal: false, formId: null }))}
    >
      <div className="mx-10 mt-4 mb-12">
        <h1 className="text-2xl font-bold mb-8">Edit form</h1>

        <form onSubmit={formik.handleSubmit}>
          <BasicInfo formik={formik} projects={projects} channels={channels} />

          <hr className={breakLineStyle} />
          <FieldPreferences formik={formik} />

          <hr className={breakLineStyle} />
          <StylesPreferences formik={formik} />

          {editingForm ? (
            <div className="relative inline-block h-full w-full mt-10">
              <ButtonLoader color="!bg-primary" />
            </div>
          ) : (
            <div className="flex justify-between mt-10">
              <Button
                inverted
                type="button"
                onClick={() => dispatch(setEditFormModal({ showModal: false, formId: null }))}
              >
                Cancel
              </Button>

              <Button type="submit">Save info</Button>
            </div>
          )}
        </form>
      </div>
    </ModalWrapper>
  );
};
